class HomeHero extends HTMLElement {
  /** @type {HTMLElement[]} */
  slides = [];

  /** @type {HTMLButtonElement[]} */
  previousButtons = [];

  /** @type {HTMLButtonElement[]} */
  nextButtons = [];

  /** @type {number} */
  index = 0;

  /** @type {number} */
  interval = 0;

  /** @type {boolean} */
  autoplay = false;

  /** @type {number | null} */
  timer = null;

  /** @type {number | null} */
  motionResetTimer = null;

  /** @type {boolean} */
  reducedMotion = false;

  connectedCallback() {
    this.slides = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-home-hero-slide]')));
    this.previousButtons = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-home-hero-prev]')));
    this.nextButtons = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-home-hero-next]')));
    this.index = this.slides.findIndex((slide) => slide.dataset.active === 'true');
    this.index = this.index >= 0 ? this.index : 0;
    this.interval = Number(this.dataset.autoplayInterval || 5) * 1000;
    this.autoplay = this.slides.length > 1 && this.interval > 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.previousButtons.forEach((button) => button.addEventListener('click', () => this.goTo(this.index - 1, true)));
    this.nextButtons.forEach((button) => button.addEventListener('click', () => this.goTo(this.index + 1, true)));

    this.addEventListener('keydown', this.handleKeydown);

    this.goTo(this.index);
    this.start();
  }

  disconnectedCallback() {
    this.pause();
    if (this.motionResetTimer) {
      window.clearTimeout(this.motionResetTimer);
      this.motionResetTimer = null;
    }

    this.removeEventListener('keydown', this.handleKeydown);
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.index - 1, true);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.index + 1, true);
    }
  };

  start = () => {
    if (!this.autoplay || this.reducedMotion) return;
    this.pause();
    this.timer = window.setInterval(() => this.goTo(this.index + 1), this.interval);
  };

  pause = () => {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  };

  /**
   * @param {number} nextIndex
   * @param {boolean} [userInitiated=false]
   */
  goTo(nextIndex, userInitiated = false) {
    if (!this.slides.length) return;

    const normalizedIndex = (nextIndex + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === normalizedIndex;
      slide.dataset.active = String(isActive);
      slide.toggleAttribute('hidden', !isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });

    this.index = normalizedIndex;
    const activeSlide = this.slides[normalizedIndex];
    if (activeSlide) {
      this.playSlideMotion(activeSlide);
    }

    if (userInitiated) {
      this.start();
    }
  }

  /**
   * @param {HTMLElement} activeSlide
   */
  playSlideMotion(activeSlide) {
    if (!activeSlide) return;

    this.slides.forEach((slide) => slide.classList.remove('is-animating'));

    if (this.motionResetTimer) {
      window.clearTimeout(this.motionResetTimer);
      this.motionResetTimer = null;
    }

    if (this.reducedMotion) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        activeSlide.classList.add('is-animating');
      });
    });

    this.motionResetTimer = window.setTimeout(() => {
      activeSlide.classList.remove('is-animating');
      this.motionResetTimer = null;
    }, 1200);
  }
}

if (!customElements.get('home-hero')) {
  customElements.define('home-hero', HomeHero);
}
